import { Outlet } from 'react-router-dom';
import { FronteggProvider, FronteggThemeOptions } from '@frontegg/react';
import { useAppAuth } from './hooks/useAppAuth';
import AuthService from './services/auth.service';
import { PliableAuthProvider } from './contexts/PliableAuthContext';
import { DataExplorerProvider } from './contexts/DataExplorerContext';

const authService = AuthService.getInstance();

// Embed sessions ALWAYS use PliableAuthProvider: Frontegg isn't part of the
// handoff (the JWT comes from /auth-exchange), and the Frontegg auth hooks
// would read empty state and redirect to login. Captured once at module
// load since client-side nav keeps App mounted after the initial route.
const isEmbedSession =
  typeof window !== 'undefined' && window.location.pathname === '/embed';

const contextOptions = {
  baseUrl: authService.authUrl,
  clientId: authService.feClientId,
  appId: authService.feAppId,
};

const authOptions = {
  keepSessionAlive: true,
  routes: {
    loginUrl: authService.useExternalLogin ? '/ext-login' : '/login',
    logoutUrl: '/do/logout',
    authenticatedUrl: '/',
  },
};

const themeOptions: FronteggThemeOptions = {
  loginBox: {},
};

// ─── App Content (shared between both providers) ─────────────────────────────

function AppContent() {
  const { isLoading, isAuthenticated } = useAppAuth();

  if (authService.useExternalLogin && isAuthenticated) {
    window.location.href = authService.authAppUrl;
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  // The data explorer belongs INSIDE the auth provider, not above the router.
  //
  // It does not render a preview of a chart — it renders Chart itself, and
  // Chart reads the viewer's roles and draft mode. Mounted above
  // RouterProvider (where App, and so the auth provider, is only a route
  // element) every one of those reads happens outside the provider: Frontegg's
  // useAuth finds a null store and throws, and "Explore from here" opens a
  // blank screen. Keep it below whichever provider App chose.
  return (
    <DataExplorerProvider>
      <Outlet />
    </DataExplorerProvider>
  );
}

// ─── App (switches provider based on OIDC mode) ─────────────────────────────

function BackendUnreachable({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <div className="max-w-md mx-auto p-6 rounded-lg border bg-card text-center space-y-3">
        <h1 className="text-lg font-semibold">Can't reach the Pliable backend</h1>
        <p className="text-sm text-muted-foreground">{message}</p>
        <p className="text-xs text-muted-foreground">
          If you're running locally, make sure the API server is up
          (<code>just run_app</code>) and reachable on the expected port.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="text-sm px-3 py-1 rounded border bg-primary text-primary-foreground hover:opacity-90"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

function App() {
  if (authService.bootstrapError) {
    return <BackendUnreachable message={authService.bootstrapError} />;
  }

  if (authService.oidcEnabled || isEmbedSession) {
    return (
      <PliableAuthProvider>
        <AppContent />
      </PliableAuthProvider>
    );
  }

  return (
    <FronteggProvider
      authOptions={authOptions}
      contextOptions={contextOptions}
      themeOptions={themeOptions}
      hostedLoginBox={false}
      entitlementsOptions={{ enabled: true }}
    >
      <AppContent />
    </FronteggProvider>
  );
}

export default App;
